import * as Sentry from "@sentry/angular";

type Primitive = string | number | boolean | null | undefined;

// Contexte optionnel à passer à logError
export interface LogContext {
  /** Tags Sentry (clé-valeur) */
  tags?: Record<string, Primitive>;
  /** Extras Sentry (clé-valeur détaillés) */
  extras?: Record<string, unknown>;
  /** Contexts Sentry (blocs structurés) */
  contexts?: Record<string, Record<string, unknown>>;
  /** Niveau de sévérité Sentry (par défaut = 'error') */
  level?: "fatal" | "error" | "warning" | "log" | "info" | "debug";
  /** Groupage manuel d'événements */
  fingerprint?: string[];
  /** Breadcrumb additionnel (catégorie, message, data, level) */
  breadcrumb?: {
    category?: string;
    message?: string;
    data?: Record<string, unknown>;
    level?: "fatal" | "error" | "warning" | "log" | "info" | "debug";
    type?: "default" | "http" | "navigation" | "user";
  };
  /** User Sentry (si tu le connais à ce moment-là) */
  user?: {
    id?: string;
    username?: string;
    email?: string;
    ip_address?: string; // évite en prod si RGPD strict
    [k: string]: unknown;
  };
  /** Ajoute un prefix visuel dans la console locale */
  consolePrefix?: string;
}

/** Champs typiques à masquer */
const SENSITIVE_KEYS = [
  "password", "pass", "authorization", "auth", "token", "accessToken", "refreshToken",
  "apiKey", "apikey", "secret", "clientSecret", "cardNumber"
];

/** Masque récursivement certaines clés sensibles */
function redact(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redact);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEYS.includes(k.toLowerCase()) ? "[REDACTED]" : redact(v);
    }
    return out;
  }
  return value;
}

/** JSON.stringify safe (réfs circulaires) */
function safeStringify(value: unknown): string {
  const seen = new WeakSet();
  return JSON.stringify(value, function (_k, v) {
    if (typeof v === "object" && v !== null) {
      if (seen.has(v)) return "[Circular]";
      seen.add(v);
    }
    return v;
  });
}

/** Convertit ...args en message style printf-like simple (si 1er arg string) */
function toFormattedMessage(args: unknown[]): string {
  if (args.length === 0) return "";
  const [first, ...rest] = args;
  if (typeof first !== "string") {
    return args.map(a => {
      if (typeof a === "string") return a;
      try { return safeStringify(redact(a)); } catch { return String(a); }
    }).join(" ");
  }
  let idx = 0;
  const formatted = first.replace(/%[sdifoO]/g, () => {
    const val = rest[idx++];
    if (val === undefined) return "undefined";
    switch (true) {
      case typeof val === "string": return val;
      case typeof val === "number": return String(val);
      case typeof val === "boolean": return String(val);
      default:
        try { return safeStringify(redact(val)); } catch { return String(val); }
    }
  });
  const leftovers = rest.slice(idx).map(v => {
    if (typeof v === "string") return v;
    try { return safeStringify(redact(v)); } catch { return String(v); }
  });
  return leftovers.length ? `${formatted} ${leftovers.join(" ")}` : formatted;
}

/** Tente d'extraire un Error depuis la liste d'arguments (ou .cause/.originalError) */
function extractError(args: unknown[]): Error | undefined {
  for (const a of args) {
    if (a instanceof Error) return a;
    if (a && typeof a === "object") {
      const obj = a as any;
      if (obj.originalError instanceof Error) return obj.originalError;
      if (obj.cause instanceof Error) return obj.cause;
      // Certains wrappers ré-encapsulent l'erreur dans obj.error
      if (obj.error instanceof Error) return obj.error;
    }
  }
  return undefined;
}

/** Construit un objet extras basé sur les args console */
function buildArgsExtras(args: unknown[]): Record<string, unknown> {
  return {
    consoleArgs: args.map(a => {
      try {
        if (typeof a === "string" || typeof a === "number" || typeof a === "boolean" || a == null) return a as Primitive;
        return JSON.parse(safeStringify(redact(a)));
      } catch {
        return String(a);
      }
    })
  };
}

/**
 * Point d'entrée: même signature que console.error, avec un 2e param optionnel LogContext
 * Usage: logError(...args)  OU  logError(ctx, ...args)
 *
 * On détecte si le 1er paramètre ressemble à un LogContext.
 */
export function logError(...all: [...Parameters<typeof console.error>] | [LogContext, ...Parameters<typeof console.error>]): void {
  // Détecte si le premier param est un contexte
  let ctx: LogContext | undefined;
  let args: Parameters<typeof console.error>;

  if (all.length && typeof all[0] === "object" && !Array.isArray(all[0]) && !("then" in all[0])) {
    // heuristique : on considère que c'est un LogContext s'il contient au moins une clé connue
    const candidate = all[0] as Record<string, unknown>;
    const hasKnownKey = ["tags","extras","contexts","level","fingerprint","breadcrumb","user","consolePrefix"].some(k => k in candidate);
    if (hasKnownKey) {
      ctx = all[0] as LogContext;
      args = all.slice(1) as Parameters<typeof console.error>;
    } else {
      args = all as Parameters<typeof console.error>;
    }
  } else {
    args = all as Parameters<typeof console.error>;
  }

  // 1) Console locale (avec éventuel prefix)
  if (ctx?.consolePrefix) {
    // eslint-disable-next-line no-console
    console.error(ctx.consolePrefix, ...args);
  } else {
    // eslint-disable-next-line no-console
    console.error(...args);
  }

  // 2) Breadcrumb optionnel (toujours utile pour le fil d'exécution)
  if (ctx?.breadcrumb) {
    Sentry.addBreadcrumb({
      category: ctx.breadcrumb.category ?? "logError",
      message: ctx.breadcrumb.message ?? (typeof args[0] === "string" ? args[0] : "logError"),
      data: ctx.breadcrumb.data ?? buildArgsExtras(args),
      level: ctx.breadcrumb.level ?? "error",
      type: ctx.breadcrumb.type ?? "default",
    });
  } else {
    // Breadcrumb minimal par défaut
    Sentry.addBreadcrumb({
      category: "logError",
      message: typeof args[0] === "string" ? args[0] : "logError",
      data: buildArgsExtras(args),
      level: "error",
    });
  }

  // 3) Capture dans Sentry
  const err = extractError(args);
  if (err) {
    Sentry.captureException(err, (scope) => {
      if (ctx?.tags) scope.setTags(ctx.tags);
      if (ctx?.extras) scope.setExtras(redact(ctx.extras) as Record<string, unknown>);
      if (ctx?.contexts) {
        for (const [name, data] of Object.entries(ctx.contexts)) {
          scope.setContext(name, redact(data) as Record<string, unknown>);
        }
      }
      if (ctx?.fingerprint) scope.setFingerprint(ctx.fingerprint);
      if (ctx?.user) scope.setUser(ctx.user as any);
      // On ajoute toujours les args console en extra pour le contexte
      scope.setExtras({ ...(ctx?.extras ?? {}), ...buildArgsExtras(args) });
      if (ctx?.level) scope.setLevel(ctx.level);
      return scope;
    });
    return;
  }

  // Pas d'Error → captureMessage avec le message formaté
  const message = toFormattedMessage(args) || "Unknown error";
  Sentry.captureMessage(message, (scope) => {
    if (ctx?.tags) scope.setTags(ctx.tags);
    if (ctx?.extras) scope.setExtras(redact(ctx.extras) as Record<string, unknown>);
    if (ctx?.contexts) {
      for (const [name, data] of Object.entries(ctx.contexts)) {
        scope.setContext(name, redact(data) as Record<string, unknown>);
      }
    }
    if (ctx?.fingerprint) scope.setFingerprint(ctx.fingerprint);
    if (ctx?.user) scope.setUser(ctx.user as any);
    scope.setExtras({ ...(ctx?.extras ?? {}), ...buildArgsExtras(args) });
    scope.setLevel(ctx?.level ?? "error");
    return scope;
  });
}
